import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  where,
} from 'firebase/firestore';
import type { Invoice, Squad } from '../../../types';
import type { PartnerLocation } from '../../partners/types';
import { db } from '../../../shared/storage/firebase';
import { useAuth } from '../../auth/contexts/AuthContext';
import { buildInvoiceLineItemsFromSquads, computeServiceRangeFromSquads, termIdForSquadStartDate } from '../../../shared/billing/squadInvoiceRollup';
import { programTermLabelFromId } from '../../../shared/terms/programTerms';
import '../styles/AdminBillingPage.css';

type PaymentMethod = 'ach' | 'check' | 'card' | 'portal' | 'other';

function safeIsoDate(v: string): string {
  return String(v || '').trim();
}

function parseDollarsToCents(v: string): number | null {
  const s = String(v || '').trim();
  if (!s) return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

function fmtUsd(centsValue: any): string {
  const n = Number(centsValue);
  if (!Number.isFinite(n)) return '—';
  return `$${(n / 100).toFixed(2)}`;
}

function invoiceIdFor(locationId: string, termId: string): string {
  return `${locationId}__${termId}`;
}

function generateInternalInvoiceNumber(params: { invoiceDate: string; schoolName: string; termId: string }): string {
  const d = safeIsoDate(params.invoiceDate) || new Date().toISOString().slice(0, 10);
  const slug =
    String(params.schoolName || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
      .slice(0, 24) || 'school';
  return `INV-${d.replace(/-/g, '')}-${slug}-${params.termId}`;
}

function squadTermId(sq: Squad): string | null {
  const explicit = typeof (sq as any).termId === 'string' ? String((sq as any).termId).trim() : '';
  if (explicit) return explicit;
  const derived = termIdForSquadStartDate(sq);
  return derived ? String(derived).trim() : null;
}

export default function AdminRecordPaymentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userData } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [step, setStep] = useState<'A' | 'B' | 'C' | 'D'>('A');

  const [locations, setLocations] = useState<PartnerLocation[]>([]);
  const [locationSearch, setLocationSearch] = useState('');
  const [locationId, setLocationId] = useState('');
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>('');
  const [directInvoiceMode, setDirectInvoiceMode] = useState(false);
  const [noInvoiceSent, setNoInvoiceSent] = useState<boolean>(false);

  const [squads, setSquads] = useState<Squad[]>([]);
  const [selectedSquadIds, setSelectedSquadIds] = useState<Record<string, boolean>>({});

  const [paidDate, setPaidDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState<PaymentMethod>('ach');
  const [amountDollars, setAmountDollars] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  // Canonical selector: locations only. Historical school data is never bulk-loaded.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const snaps = await getDocs(collection(db, 'locations'));
        if (cancelled) return;
        const next: PartnerLocation[] = [];
        snaps.forEach((s) => {
          const d = s.data() as any;
          next.push({ ...(d as any), id: s.id } as PartnerLocation);
        });
        next.sort((a, b) => String((a as any).name || '').localeCompare(String((b as any).name || '')));
        setLocations(next);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message ? String(e.message) : 'Failed to load locations.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const invoiceId = String(searchParams.get('invoice') || '').trim();
    if (!invoiceId) return;
    let cancelled = false;
    const loadInvoice = async () => {
      setLoading(true);
      setError(null);
      try {
        const snap = await getDoc(doc(db, 'invoices', invoiceId));
        if (!snap.exists()) throw new Error('Invoice not found.');
        const value = { ...(snap.data() as any), id: snap.id } as Invoice;
        if (value.legacy === true) throw new Error('Legacy invoices are read-only.');
        if (cancelled) return;
        setDirectInvoiceMode(true);
        setLocationId(String(value.locationId || 'partner-wide'));
        setInvoices([value]);
        setSelectedInvoiceId(value.id);
        setNoInvoiceSent(false);
        setAmountDollars((Math.max(0, Number(value.totalCents || 0) - Number(value.paidAmountCents || 0)) / 100).toFixed(2));
        setMethod((value.paidMethod as PaymentMethod) || 'ach');
        setStep('D');
      } catch (nextError) {
        if (!cancelled) setError(nextError instanceof Error ? nextError.message : 'Invoice could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void loadInvoice();
    return () => { cancelled = true; };
  }, [searchParams]);

  // Load canonical invoices + squads for the selected location.
  useEffect(() => {
    if (directInvoiceMode) return;
    let cancelled = false;
    const run = async () => {
      const lid = String(locationId || '').trim();
      if (!lid) {
        setInvoices([]);
        setSelectedInvoiceId('');
        setNoInvoiceSent(false);
        setSquads([]);
        setSelectedSquadIds({});
        setStep('A');
        return;
      }
      setLoading(true);
      setError(null);
      setInfo(null);
      try {
        const [canonicalInvSnaps, sqSnaps] = await Promise.all([
          getDocs(query(collection(db, 'invoices'), where('locationId', '==', lid))),
          getDocs(query(collection(db, 'squads'), where('locationId', '==', lid))),
        ]);
        if (cancelled) return;
        const invs: Invoice[] = [];
        canonicalInvSnaps.forEach((s) => invs.push({ ...(s.data() as any), id: s.id } as Invoice));
        const currentInvoices = invs.filter((invoice) => invoice.legacy !== true && Boolean(String(invoice.partnerId || '').trim()));
        currentInvoices.sort((a, b) => String(a.termId || '').localeCompare(String(b.termId || '')));
        setInvoices(currentInvoices);

        const sqs: Squad[] = [];
        sqSnaps.forEach((s) => sqs.push({ ...(s.data() as any), id: s.id } as Squad));
        // Only show squads that are billed to school (exclude stripe-only)
        const billable = sqs.filter((sq) => {
          const mode = String((sq as any).paymentMode || '').trim().toLowerCase();
          return mode === 'school_flat_fee' || mode === 'school_enrollment' || mode === 'external';
        });
        billable.sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')));
        setSquads(billable);
        setSelectedSquadIds({});

        // When switching locations, reset downstream state and return to Step B.
        setSelectedInvoiceId('');
        setNoInvoiceSent(false);
        setAmountDollars('');
        setNotes('');
        setMethod('ach');
        setPaidDate(new Date().toISOString().slice(0, 10));
        setStep('B');
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message ? String(e.message) : 'Failed to load billing data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [directInvoiceMode, locationId]);

  const filteredLocations = useMemo(() => {
    const q = String(locationSearch || '').trim().toLowerCase();
    if (!q) return locations;
    return locations.filter((item) => String(item.name || '').toLowerCase().includes(q));
  }, [locations, locationSearch]);

  const selectedLocation = useMemo(() => {
    const lid = String(locationId || '').trim();
    return locations.find((item) => item.id === lid) || null;
  }, [locations, locationId]);

  const selectedLocationLabel = useMemo(() => {
    return selectedLocation ? String(selectedLocation.name || selectedLocation.id) : '';
  }, [selectedLocation]);

  const selectedInvoice = useMemo(() => {
    const id = String(selectedInvoiceId || '').trim();
    if (!id) return null;
    return invoices.find((i) => i.id === id) || null;
  }, [invoices, selectedInvoiceId]);

  const selectedSquads = useMemo(() => {
    const ids = selectedSquadIds || {};
    return squads.filter((s) => Boolean(ids[s.id]));
  }, [squads, selectedSquadIds]);

  const selectedSquadsTermId = useMemo((): string | null => {
    const terms = new Set<string>();
    for (const sq of selectedSquads) {
      const tid = squadTermId(sq);
      if (!tid) return null;
      terms.add(tid);
    }
    if (terms.size === 0) return null;
    if (terms.size > 1) return '__MULTI__';
    return Array.from(terms)[0];
  }, [selectedSquads]);

  const computedLineItems = useMemo(() => buildInvoiceLineItemsFromSquads({ squads: selectedSquads }), [selectedSquads]);
  const computedTotalCents = useMemo(() => computedLineItems.reduce((sum, li) => sum + Number(li.amountCents || 0), 0), [computedLineItems]);
  const computedServiceRange = useMemo(() => computeServiceRangeFromSquads(selectedSquads), [selectedSquads]);

  const prefillFromInvoice = useCallback((inv: Invoice | null) => {
    if (!inv) return;
    const total = Number((inv as any).totalCents);
    if (Number.isFinite(total)) setAmountDollars((total / 100).toFixed(2));
    const existingMethod = String((inv as any).paidMethod || '').trim().toLowerCase();
    if (existingMethod === 'ach' || existingMethod === 'check' || existingMethod === 'card' || existingMethod === 'portal' || existingMethod === 'other') {
      setMethod(existingMethod as PaymentMethod);
    } else {
      setMethod('ach');
    }
    const existingNotes = String((inv as any).paidNotes || '').trim();
    setNotes(existingNotes);
    const existingPaidAt = (inv as any).paidAt;
    try {
      if (existingPaidAt && typeof existingPaidAt?.toDate === 'function') {
        const d = existingPaidAt.toDate();
        if (d instanceof Date && !Number.isNaN(d.getTime())) setPaidDate(d.toISOString().slice(0, 10));
      }
    } catch (_) {}
  }, []);

  const prefillFromSquads = useCallback(() => {
    if (computedTotalCents > 0) setAmountDollars((computedTotalCents / 100).toFixed(2));
    setMethod('ach');
    setNotes('');
    setPaidDate(new Date().toISOString().slice(0, 10));
  }, [computedTotalCents]);

  const toggleSquad = useCallback((id: string) => {
    setSelectedSquadIds((prev) => ({ ...(prev || {}), [id]: !prev?.[id] }));
  }, []);

  const recordPayment = useCallback(async () => {
    const lid = String(locationId || '').trim();
    if (!lid && !selectedInvoice) {
      setError('Select a location.');
      return;
    }

    const paidYmd = safeIsoDate(paidDate);
    if (!paidYmd || !/^\d{4}-\d{2}-\d{2}$/.test(paidYmd)) {
      setError('Enter a valid paid date.');
      return;
    }

    const cents = parseDollarsToCents(amountDollars);
    if (cents == null || cents < 0) {
      setError('Enter a valid amount.');
      return;
    }

    if (!method) {
      setError('Select a payment method.');
      return;
    }

    const isNoInvoice = Boolean(noInvoiceSent);
    const invId = String(selectedInvoiceId || '').trim();
    if (!isNoInvoice && !invId) {
      setError('Select an invoice, or choose “No invoice sent”.');
      return;
    }

    if (isNoInvoice) {
      if (selectedSquads.length === 0) {
        setError('Select the squads covered by the payment.');
        return;
      }
      if (selectedSquadsTermId === '__MULTI__') {
        setError('Selected squads span multiple terms. Please select squads from a single term.');
        return;
      }
      if (!selectedSquadsTermId) {
        setError('Could not determine a term for one or more selected squads.');
        return;
      }
    }

    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const paidAt = Timestamp.fromDate(new Date(`${paidYmd}T00:00:00`));
      const uid = String((userData as any)?.id || (userData as any)?.uid || '').trim();
      const paidBy = uid || String((userData as any)?.email || '').trim() || 'admin';

      // If an invoice is selected, just mark it paid (keep existing structure).
      if (!isNoInvoice && selectedInvoice) {
        const ref = doc(db, 'invoices', selectedInvoice.id);
        await runTransaction(db, async (tx) => {
          const snap = await tx.get(ref);
          if (!snap.exists()) throw new Error('Invoice not found.');
          tx.update(ref, {
            paidAt,
            paidBy,
            paidAmountCents: cents,
            paidMethod: method,
            paidNotes: String(notes || '').trim() || null,
            updatedAt: Timestamp.now(),
            updatedBy: paidBy,
            status: 'paid',
          } as any);
        });
        setInfo('Payment recorded.');
        navigate(`/app/admin/invoices/${selectedInvoice.id}`);
        return;
      }

      // No invoice sent: create the missing canonical invoice record for the location+term.
      const termId = String(selectedSquadsTermId || '').trim();
      const invDocId = invoiceIdFor(lid, termId);
      const ref = doc(db, 'invoices', invDocId);
      const locationName = String(selectedLocation?.name || '').trim();
      const partnerId = String(selectedLocation?.partnerId || '').trim();
      if (!partnerId) throw new Error('This location has not been linked to a canonical partner.');
      const invDate = paidYmd; // For retroactive records, treat invoiceDate as paid date.

      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (snap.exists()) {
          // Safety: if it exists, the admin should pick it explicitly to avoid overwriting.
          throw new Error('An invoice record already exists for this school + term. Select the invoice instead of “No invoice sent”.');
        }

        const lineItems = computedLineItems;
        const totalCents = computedTotalCents;
        const svc = computedServiceRange || { startDate: undefined, endDate: undefined };

        tx.set(ref, {
          id: invDocId,
          legacy: false,
          partnerId,
          locationId: lid,
          termId,
          invoiceDate: invDate,
          invoiceSendDate: invDate,
          paymentDueDate: invDate,
          currency: 'USD',
          lineItems,
          subtotalCents: totalCents,
          totalCents,
          schoolNameSnapshot: locationName || undefined,
          schoolTermSnapshot: programTermLabelFromId(termId) || undefined,
          serviceDatesSnapshot: { startDate: svc?.startDate, endDate: svc?.endDate },
          serviceDescriptionSnapshot: 'Program tuition',
          paymentDescriptionSnapshot: 'Tuition',
          internalInvoiceNumberSnapshot: generateInternalInvoiceNumber({ invoiceDate: invDate, schoolName: locationName || 'location', termId }),
          paidAt,
          paidBy,
          paidAmountCents: cents,
          paidMethod: method,
          paidNotes: String(notes || '').trim() || null,
          status: 'paid',
          createdAt: Timestamp.now(),
          createdBy: paidBy,
          updatedAt: Timestamp.now(),
          updatedBy: paidBy,
        } as any);
      });

      setInfo('Payment recorded (invoice record created).');
      navigate(`/app/admin/invoices/${invDocId}`);
    } catch (e: any) {
      setError(e?.message ? String(e.message) : 'Failed to record payment.');
    } finally {
      setSaving(false);
    }
  }, [
    amountDollars,
    computedLineItems,
    computedServiceRange,
    computedTotalCents,
    method,
    navigate,
    noInvoiceSent,
    notes,
    paidDate,
    locationId,
    selectedInvoice,
    selectedInvoiceId,
    selectedLocation,
    selectedSquads,
    selectedSquadsTermId,
    userData,
  ]);

  const termLabel = useMemo(() => {
    if (noInvoiceSent) {
      if (selectedSquadsTermId && selectedSquadsTermId !== '__MULTI__') return programTermLabelFromId(selectedSquadsTermId);
      if (selectedSquadsTermId === '__MULTI__') return 'Multiple terms';
      return '—';
    }
    return programTermLabelFromId(String(selectedInvoice?.termId || ''));
  }, [noInvoiceSent, selectedInvoice?.termId, selectedSquadsTermId]);

  const canNextA = Boolean(String(locationId || '').trim());
  const canNextB = Boolean(noInvoiceSent || String(selectedInvoiceId || '').trim());
  const canNextC =
    Boolean(noInvoiceSent)
    && selectedSquads.length > 0
    && selectedSquadsTermId != null
    && selectedSquadsTermId !== '__MULTI__';

  const goBack = useCallback(() => {
    setError(null);
    setInfo(null);
    setStep((cur) => {
      if (cur === 'D') return noInvoiceSent ? 'C' : 'B';
      if (cur === 'C') return 'B';
      if (cur === 'B') return 'A';
      return 'A';
    });
  }, [noInvoiceSent]);

  const goNext = useCallback(() => {
    setError(null);
    setInfo(null);
    setStep((cur) => {
      if (cur === 'A') return 'B';
      if (cur === 'B') return noInvoiceSent ? 'C' : 'D';
      if (cur === 'C') return 'D';
      return 'D';
    });
  }, [noInvoiceSent]);

  return (
    <div className="billing-page billing-page--fullscreen">
      <div className="billing-card">
        <div className="billing-header">
          <button
            className="billing-back"
            onClick={() => navigate('/app/admin', { state: { adminTab: 'invoicing' } })}
            disabled={saving || loading}
          >
            ← Back
          </button>
          <div style={{ flex: 1 }}>
            <div className="billing-title">Record payment</div>
            <div className="billing-subtitle">{directInvoiceMode ? 'Record and reconcile a payment against this partner invoice.' : 'Find an invoice to mark paid, or use the exception path for a payment without an invoice.'}</div>
          </div>
        </div>

        {error && <div className="billing-error">{error}</div>}
        {info && <div className="billing-info">{info}</div>}

        {step === 'A' ? (
          <div className="billing-section">
            <div className="billing-section-header">
              <div className="billing-section-title">Select location</div>
            </div>

            <div>
              <div style={{ position: 'relative' }}>
                <input
                  className="billing-input"
                  value={locationSearch}
                  onChange={(e) => {
                    const next = e.target.value;
                    setLocationSearch(next);
                    setLocationPickerOpen(true);
                    if (locationId && selectedLocationLabel && next.trim() !== selectedLocationLabel.trim()) {
                      setLocationId('');
                      setInvoices([]);
                      setSelectedInvoiceId('');
                      setNoInvoiceSent(false);
                      setSquads([]);
                      setSelectedSquadIds({});
                      setStep('A');
                    }
                  }}
                  onFocus={() => setLocationPickerOpen(true)}
                  onBlur={() => {
                    // allow click selection from the dropdown
                    window.setTimeout(() => setLocationPickerOpen(false), 150);
                    if (locationId && selectedLocationLabel) {
                      window.setTimeout(() => setLocationSearch(selectedLocationLabel), 0);
                    }
                  }}
                  placeholder="Search canonical locations…"
                  disabled={loading || saving}
                  role="combobox"
                  aria-expanded={locationPickerOpen}
                  aria-controls="record-payment-location-options"
                  aria-autocomplete="list"
                />

                {locationPickerOpen && filteredLocations.length > 0 ? (
                  <div
                    id="record-payment-location-options"
                    role="listbox"
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 6px)',
                      left: 0,
                      right: 0,
                      zIndex: 20,
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: 10,
                      boxShadow: '0 10px 24px rgba(0,0,0,0.08)',
                      maxHeight: 260,
                      overflow: 'auto',
                    }}
                  >
                    {filteredLocations.slice(0, 12).map((s) => {
                      const label = String((s as any).name || s.id);
                      const isActive = locationId === s.id;
                      return (
                        <div
                          key={s.id}
                          role="option"
                          aria-selected={isActive}
                          onMouseDown={(e) => {
                            // prevent input blur before we handle selection
                            e.preventDefault();
                          }}
                          onClick={() => {
                            setLocationId(s.id);
                            setLocationSearch(label);
                            setLocationPickerOpen(false);
                            setError(null);
                            setInfo(null);
                          }}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            fontWeight: isActive ? 700 : 500,
                            background: isActive ? 'rgba(59,130,246,0.08)' : 'transparent',
                            borderBottom: '1px solid #f3f4f6',
                          }}
                          title={label}
                        >
                          {label}
                        </div>
                      );
                    })}
                    {filteredLocations.length > 12 ? (
                      <div style={{ padding: '8px 12px', fontSize: 12, color: '#6b7280' }}>
                        Keep typing to narrow results…
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {selectedLocation ? (
                <div className="muted" style={{ marginTop: 10 }}>
                  Selected: <b>{String(selectedLocation.name || selectedLocation.id)}</b>
                </div>
              ) : null}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
              <button className="btn btn-primary" onClick={goNext} disabled={loading || saving || !canNextA}>Next</button>
            </div>
          </div>
        ) : null}

        {step === 'B' ? (
          <div className="billing-section">
            <div className="billing-section-header">
              <div className="billing-section-title">Apply to invoice</div>
            </div>

            <select
              className="billing-select"
              value={selectedInvoiceId}
              onChange={(e) => {
                const nextId = e.target.value;
                setSelectedInvoiceId(nextId);
                setNoInvoiceSent(false);
                setSelectedSquadIds({});
                setInfo(null);
                setError(null);
                const inv = invoices.find((i) => i.id === nextId) || null;
                prefillFromInvoice(inv);
              }}
              disabled={!locationId || loading || saving || noInvoiceSent}
            >
              <option value="">Select an invoice…</option>
              {invoices.map((inv) => {
                const label = `${programTermLabelFromId(inv.termId)} • ${String((inv as any).internalInvoiceNumberSnapshot || inv.id)}`;
                return <option key={inv.id} value={inv.id}>{label}</option>;
              })}
            </select>

            <label className="billing-checkbox" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
              <input
                type="checkbox"
                checked={noInvoiceSent}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setNoInvoiceSent(checked);
                  setSelectedInvoiceId('');
                  setSelectedSquadIds({});
                  setInfo(null);
                  setError(null);
                  if (checked) prefillFromSquads();
                }}
                disabled={!locationId || loading || saving}
              />
              <span>No invoice sent</span>
            </label>

            {selectedInvoice ? (
              <div className="muted" style={{ marginTop: 10 }}>
                Term: <b>{programTermLabelFromId(selectedInvoice.termId)}</b> • Total: <b>{fmtUsd((selectedInvoice as any).totalCents)}</b>
              </div>
            ) : null}
            {noInvoiceSent ? (
              <div className="muted" style={{ marginTop: 10 }}>
                You’ll select squads next. We’ll create the missing invoice record for that school+term and mark it paid.
              </div>
            ) : null}

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 14 }}>
              <button className="btn btn-secondary" onClick={goBack} disabled={loading || saving}>Back</button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (!canNextB) {
                    setError('Select an invoice, or choose “No invoice sent”.');
                    return;
                  }
                  goNext();
                }}
                disabled={loading || saving || !canNextB}
              >
                Next
              </button>
            </div>
          </div>
        ) : null}

        {step === 'C' && noInvoiceSent ? (
          <div className="billing-section">
            <div className="billing-section-header">
              <div className="billing-section-title">Select squads covered</div>
              <div className="muted">Term: <b>{termLabel}</b></div>
            </div>

            <div className="muted" style={{ marginBottom: 10 }}>
              Required when “No invoice sent” is selected.
            </div>

            <div style={{ maxHeight: 320, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 10 }}>
              <table className="billing-table">
                <thead>
                  <tr>
                    <th style={{ width: 48 }} />
                    <th>Squad</th>
                    <th>Term</th>
                    <th>Dates</th>
                    <th>Mode</th>
                    <th style={{ textAlign: 'right' }}>Enrolled</th>
                  </tr>
                </thead>
                <tbody>
                  {squads.length === 0 ? (
                    <tr><td colSpan={6} className="muted">No billable squads found for this school.</td></tr>
                  ) : squads.map((sq) => {
                    const checked = Boolean(selectedSquadIds[sq.id]);
                    const tid = squadTermId(sq);
                    const mode = String((sq as any).paymentMode || '').trim() || '—';
                    const enrolled = Array.isArray((sq as any).studentIds) ? (sq as any).studentIds.length : 0;
                    const start = String(sq.schedule?.mainDate || sq.schedule?.startDate || '').trim();
                    const end = String(sq.schedule?.endDate || '').trim();
                    return (
                      <tr key={sq.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              toggleSquad(sq.id);
                              // keep amount roughly aligned if admin hasn't set it yet (we only set when empty)
                              if (!String(amountDollars || '').trim()) {
                                setTimeout(() => prefillFromSquads(), 0);
                              }
                            }}
                            disabled={!locationId || loading || saving}
                          />
                        </td>
                        <td>{sq.title || sq.id}</td>
                        <td className="muted">{tid ? programTermLabelFromId(tid) : '—'}</td>
                        <td className="muted">{start && end ? `${start} → ${end}` : (start || end || '—')}</td>
                        <td className="muted">{mode}</td>
                        <td style={{ textAlign: 'right' }} className="muted">{enrolled}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {selectedSquadsTermId === '__MULTI__' ? (
              <div className="billing-error" style={{ marginTop: 10 }}>
                Selected squads span multiple terms. Please select squads from a single term.
              </div>
            ) : null}

            {selectedSquads.length > 0 ? (
              <div className="muted" style={{ marginTop: 10 }}>
                Selected: <b>{selectedSquads.length}</b> squads • Computed total (from squads): <b>{fmtUsd(computedTotalCents)}</b>
                {computedServiceRange?.startDate || computedServiceRange?.endDate ? (
                  <> • Dates: <b>{String(computedServiceRange?.startDate || '—')} → {String(computedServiceRange?.endDate || '—')}</b></>
                ) : null}
              </div>
            ) : null}

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 14 }}>
              <button className="btn btn-secondary" onClick={goBack} disabled={loading || saving}>Back</button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (!canNextC) {
                    setError('Select squads from a single term.');
                    return;
                  }
                  goNext();
                }}
                disabled={loading || saving || !canNextC}
              >
                Next
              </button>
            </div>
          </div>
        ) : null}

        {step === 'D' ? (
          <div className="billing-section">
            <div className="billing-section-header">
              <div className="billing-section-title">Payment details</div>
            </div>

            <div className="muted" style={{ marginBottom: 10 }}>
              {noInvoiceSent ? (
                <>Applies to: <b>{String(selectedLocation?.name || locationId)}</b> • Term: <b>{termLabel}</b></>
              ) : selectedInvoice ? (
                <>Applies to: <b>{String(selectedLocation?.name || locationId)}</b> • Invoice: <b>{String((selectedInvoice as any).internalInvoiceNumberSnapshot || selectedInvoice.id)}</b></>
              ) : (
                <>Applies to: <b>{String(selectedLocation?.name || locationId)}</b></>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div>
                <div className="muted" style={{ marginBottom: 6 }}>Paid date</div>
                <input
                  className="billing-input"
                  type="date"
                  value={paidDate}
                  onChange={(e) => setPaidDate(e.target.value)}
                  disabled={loading || saving}
                />
              </div>
              <div>
                <div className="muted" style={{ marginBottom: 6 }}>Method</div>
                <select
                  className="billing-select"
                  value={method}
                  onChange={(e) => setMethod(e.target.value as PaymentMethod)}
                  disabled={loading || saving}
                >
                  <option value="ach">ACH</option>
                  <option value="check">Check</option>
                  <option value="card">Card</option>
                  <option value="portal">Portal</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <div className="muted" style={{ marginBottom: 6 }}>Amount (USD)</div>
                <input
                  className="billing-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={amountDollars}
                  onChange={(e) => setAmountDollars(e.target.value)}
                  disabled={loading || saving}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <div className="muted" style={{ marginBottom: 6 }}>Notes (optional)</div>
              <textarea
                className="billing-textarea"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={loading || saving}
                placeholder="ACH trace / check # / context…"
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 14 }}>
              <button className="btn btn-secondary" onClick={goBack} disabled={loading || saving}>Back</button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  // Final gate: ensure squads are selected if no-invoice.
                  if (noInvoiceSent) {
                    if (selectedSquads.length === 0 || selectedSquadsTermId == null || selectedSquadsTermId === '__MULTI__') {
                      setError('Select squads from a single term.');
                      setStep('C');
                      return;
                    }
                  }
                  recordPayment();
                }}
                disabled={loading || saving || !locationId || !canNextB}
              >
                {saving ? 'Saving…' : 'Record payment'}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
